import { Box, MenuItem, TextField } from "@mui/material"
import { Mail, FileText, Newspaper, Video, Layout, BookOpen, BookImage, Presentation } from "lucide-react"
import { EMAIL_LAYOUTS, isKnownLayout, type EmailLayoutType } from "@crm-src/lib/email-layouts"

interface EmailLayoutSelectorProps {
  selectedLayout: EmailLayoutType | undefined
  onLayoutChange: (layout: EmailLayoutType) => void
  disabled?: boolean
}

export default function EmailLayoutSelector({
  selectedLayout,
  onLayoutChange,
  disabled = false
}: EmailLayoutSelectorProps) {
  const layoutIcons: Record<EmailLayoutType, any> = {
    olive_green_newsletter: Newspaper,
    simple_text: FileText,
    webinar: Video,
    case_study: FileText,
    ceo_letter: Mail,
    masthead_newsletter: Layout,
    brochure: BookOpen,
    use_case_brochure: BookImage,
    spotlight_webinar: Presentation
  }

  const handleLayoutSelect = (layoutId: string) => {
    onLayoutChange(layoutId as EmailLayoutType)
  }

  return (
    <TextField
      select
      size="small"
      fullWidth
      value={selectedLayout || ""}
      onChange={(e) => handleLayoutSelect(e.target.value)}
      disabled={disabled}
      SelectProps={{
        displayEmpty: true,
        renderValue: (value) => {
          const layout = value as EmailLayoutType | ""
          if (!layout) {
            return <Box component="span" sx={{ color: "text.disabled" }}>Select template</Box>
          }
          // A template newer than this build (see isKnownLayout) — name it
          // instead of crashing the page.
          if (!isKnownLayout(layout)) {
            return (
              <Box component="span" sx={{ color: "text.secondary" }}>
                {String(layout)} (not in this version of the app)
              </Box>
            )
          }
          const Icon = layoutIcons[layout]
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon size={16} />
              <Box component="span">{EMAIL_LAYOUTS[layout].name}</Box>
            </Box>
          )
        },
      }}
    >
      {Object.values(EMAIL_LAYOUTS).map((layout) => {
        const Icon = layoutIcons[layout.id]
        return (
          <MenuItem key={layout.id} value={layout.id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
              <Icon size={16} />
              <Box component="span" sx={{ fontWeight: 500 }}>{layout.name}</Box>
            </Box>
          </MenuItem>
        )
      })}
    </TextField>
  )
}
